// =============================================================================
// announcements-admin-client.ts — Client API ANNONCES ADMIN (diffusion globale par campagne), DOUBLABLE.
// =============================================================================
// R15-COM-03 (DBU §2.7.3) — surface ADMIN du COMPOSER de diffusion globale : brouillon + planification +
// envoi immédiat + fenêtre de modification + registre + annulation. Miroir des routes @AdminRoute du BC
// communication (AnnouncementController, écriture sous withAdmin — agrégat admin-scope, parité legal.cgu_*).
//
// Comme les autres clients UI (legal-admin, campaign, notifications), expose une INTERFACE consommée par
// les hooks TanStack ; l'impl HTTP réelle (fetch) vit derrière la MÊME interface (DI seam). AUCUNE règle
// métier ici : le client appelle, parse, mappe l'erreur. L'AUTORITÉ (états, transitions, éligibilité
// campagne, fan-out) est l'API. Les DTO sont des MIROIRS HTTP (PAS le Published Language, PAS @jeko/db).
//
// DEC-35 (GATE_HUMAN) : l'annonce porte `titre` (obligatoire au composer) + `nombreDestinataires` (rempli
// au fan-out ; null tant que non envoyée). Il n'existe PAS de « date de création » en base — hors périmètre.

import { HttpApiError } from '@/api/http-error';

/**
 * États de l'annonce (enum backend `announcement_state`, verbatim §5.2.7). Cycle de vie :
 *   Brouillon → Planifiee (envoi différé) | Modifiable (envoi immédiat, fenêtre de modif ouverte)
 *   Modifiable → Verrouillee (fenêtre écoulée, terminal) ; Planifiee → Annulee (terminal).
 * Le LIBELLÉ FR (announcement-labels) est l'unique porteur d'information ; la couleur n'est qu'un renfort.
 */
export type AnnouncementStateDto =
  | 'Brouillon'
  | 'Planifiee'
  | 'Modifiable'
  | 'Verrouillee'
  | 'Annulee';

/**
 * DTO d'une annonce renvoyé par l'API (miroir HTTP). `campaignIntitule` est un enrichissement de confort
 * (composition read-side côté API/port) : absent si non résolu → l'UI dégrade sur `campaignId`. `createdBy`
 * est un subject OIDC admin OPAQUE (aucun endpoint de résolution nom — dégradation assumée, invariant n°2).
 */
export interface AnnouncementDto {
  readonly announcementId: string;
  readonly campaignId: string;
  /** Intitulé de la campagne cible (confort read-side) ; absent si non résolu. */
  readonly campaignIntitule?: string;
  readonly state: AnnouncementStateDto;
  /** Titre de l'annonce (DEC-35 : obligatoire au composer). */
  readonly titre: string;
  /** Corps HTML éditorial (WYSIWYG). NETTOYÉ côté serveur au write (choke point autoritaire). */
  readonly contentRich: string;
  /** Échéance d'envoi différé (ISO 8601), ou null (envoi immédiat / non planifié). */
  readonly scheduledFor: string | null;
  /** Horodatage du fan-out (ISO 8601), ou null tant que non envoyée. */
  readonly sentAt: string | null;
  /** Expiration de la fenêtre de modification (ISO 8601 = sentAt + fenêtre), ou null. */
  readonly modificationWindowExpiresAt: string | null;
  /** Nombre de destinataires (DEC-35 : rempli au fan-out) ; null tant que non envoyée. */
  readonly nombreDestinataires: number | null;
  /** Administrateur auteur (subject OIDC OPAQUE, non résolu côté serveur), ou null. */
  readonly createdBy: string | null;
}

/**
 * Campagne éligible comme cible de diffusion (miroir de /admin/announcements/eligible-campaigns). L'API
 * NE renvoie QUE les campagnes éligibles (états Ouverte/Analyse Cycle/Négociation, DBU §2.7.3 l.1700) : le
 * filtrage d'éligibilité est SERVEUR (port Published Language campagne, jamais deep-import). L'UI liste.
 */
export interface EligibleCampaignDto {
  readonly campaignId: string;
  readonly intitule: string;
  /** État de la campagne (renfort informatif ; l'éligibilité est déjà garantie serveur). */
  readonly state: string;
}

/** Corps de création d'une annonce (miroir POST /admin/announcements) → l'annonce créée (Brouillon). */
export interface CreateAnnouncementInput {
  readonly campaignId: string;
  /** Titre (DEC-35 : obligatoire). */
  readonly titre: string;
  /** Corps HTML éditorial (obligatoire au composer). NETTOYÉ côté serveur. */
  readonly contentRich: string;
}

/**
 * Patch PARTIEL du titre/contenu — sert l'AUTOSAVE du brouillon (PATCH /admin/announcements/:id) ET
 * l'édition inline pendant la fenêtre de modification (PATCH /admin/announcements/:id/content). Un champ
 * absent = inchangé côté serveur. Le HTML est NETTOYÉ par l'API (défense XSS) ; le front ne sanitise pas.
 */
export interface AnnouncementContentPatch {
  readonly titre?: string;
  readonly contentRich?: string;
}

/**
 * Filtres du registre (query params de GET /admin/announcements). `from`/`to` = bornes de PÉRIODE au format
 * `YYYY-MM-DD` (côté API, filtrage sur la date d'envoi). Tout filtre absent = non contraint. État de VUE
 * porté par l'URL côté écran (RULE_REF_006 : pas de localStorage).
 */
export interface AnnouncementListFilters {
  readonly state?: AnnouncementStateDto;
  readonly campaignId?: string;
  readonly from?: string;
  readonly to?: string;
}

/**
 * Erreur applicative renvoyée par l'API annonces admin (HTTP 4xx). Porte le `status` HTTP pour que l'UI
 * mappe la règle métier PAR CODE (404 = annonce absente ; 409 = transition illégale, ex. annuler une
 * annonce non-Planifiee, ou éditer hors fenêtre ; 400 = contenu/planification invalide). Elle ne
 * réinterprète JAMAIS la règle métier (autorité = API).
 */
export class AnnouncementsAdminApiError extends HttpApiError {
  public constructor(message: string, status?: number) {
    super(message, status, 'AnnouncementsAdminApiError');
  }
}

/**
 * Traduit une erreur d'écriture d'annonce en message utilisateur. 404/409/400 relaient le message serveur
 * (déjà en français, propre, non sensible) ; toute autre erreur retombe sur `fallback`. Ne masque rien de
 * plus que nécessaire (les messages du BC communication ne portent pas de PII).
 */
export function announcementWriteErrorMessage(
  error: unknown,
  fallback: string,
): string {
  if (error instanceof AnnouncementsAdminApiError) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return fallback;
}

/**
 * Contrat du client ANNONCES ADMIN. Toutes les écritures sont ADMIN-guarded (cookie admin httpOnly +
 * double-submit `admin_csrf`), calque des autres clients admin (legal-admin, catalog-edition). L'impl
 * réelle est HTTP ; les tests injectent un double via le DI seam (AnnouncementsAdminClientProvider).
 */
export interface AnnouncementsAdminClient {
  /** GET /admin/announcements — registre (filtres statut/campagne/période en query params). */
  list(filters?: AnnouncementListFilters): Promise<readonly AnnouncementDto[]>;
  /** GET /admin/announcements/eligible-campaigns — campagnes cibles éligibles (filtrage serveur). */
  eligibleCampaigns(): Promise<readonly EligibleCampaignDto[]>;
  /** POST /admin/announcements — crée un BROUILLON (titre + contenu + campagne). */
  create(input: CreateAnnouncementInput): Promise<AnnouncementDto>;
  /** PATCH /admin/announcements/:id — AUTOSAVE silencieux du brouillon (patch partiel titre/contenu). */
  updateDraft(
    announcementId: string,
    patch: AnnouncementContentPatch,
  ): Promise<AnnouncementDto>;
  /** POST /admin/announcements/:id/schedule — planifie l'envoi différé (Brouillon → Planifiee). */
  schedule(announcementId: string, scheduledFor: string): Promise<AnnouncementDto>;
  /** POST /admin/announcements/:id/publish-now — déclenche l'envoi immédiat (Brouillon → Modifiable). */
  publishNow(announcementId: string): Promise<AnnouncementDto>;
  /** PATCH /admin/announcements/:id/content — édition inline pendant la fenêtre de modification. */
  editContent(
    announcementId: string,
    patch: AnnouncementContentPatch,
  ): Promise<AnnouncementDto>;
  /** POST /admin/announcements/:id/cancel — annule une annonce Planifiee (Planifiee → Annulee). */
  cancel(announcementId: string): Promise<AnnouncementDto>;
}
